interface CountFittingChipsParams {
  /** Natural (unshrunk) width of every chip, in render order. */
  chipWidths: number[]
  /** Width of the "+N" counter chip. */
  counterWidth: number
  /** Gap between neighbouring chips. */
  gap: number
  availableWidth: number
}

/**
 * How many chips fit on one row of `availableWidth`, keeping room for a trailing "+N" counter
 * whenever some chips are left out. The counter is only needed when chips are hidden, so the last
 * chip may use the space the counter would have taken.
 *
 * Never returns less than 1 for a non-empty list: a lone chip that is too wide is still shown and
 * left to ellipsize, since a bare "+N" with no chip beside it reads as an empty field.
 */
export const countFittingChips = ({
  chipWidths,
  counterWidth,
  gap,
  availableWidth,
}: CountFittingChipsParams): number => {
  const totalWidth = chipWidths.reduce((sum, chipWidth, index) => {
    return sum + chipWidth + (index > 0 ? gap : 0)
  }, 0)

  if (totalWidth <= availableWidth) {
    return chipWidths.length
  }

  // Some chips will be hidden, so the counter is always shown from here on.
  let usedWidth = counterWidth

  for (let index = 0; index < chipWidths.length; index += 1) {
    const nextWidth = usedWidth + gap + chipWidths[index]

    if (nextWidth > availableWidth) {
      return Math.max(index, 1)
    }

    usedWidth = nextWidth
  }

  return chipWidths.length
}
